import { z } from 'zod';
import { criterionContentSchema } from './criterion.js';
import { experienceLocalIdSchema, richTextSchema } from './experience.js';
import { noyauDeterminationSchema } from './noyau-session.js';
import {
  noyauBlockModeSchema,
  noyauCardIdSchema,
  noyauChoiceModeSchema,
  noyauCodeSchema,
  noyauMaxGapSchema,
} from './noyau.js';
import { protocolContentSchema } from './protocol.js';
import { sessionRangementSchema } from './rangement-session.js';
import { sessionReplayFullSchema } from './replay-mark.js';
import { sessionRowSchema } from './session-play.js';
import { sessionPageVisitSchema } from './session.js';
import { sessionTurnSchema, turnCallSchema } from './turn.js';
import { versionPinSchema } from './versioning.js';

/**
 * READING a session (refonte modulaire §12.2, §12.3, §12.5) - what the three
 * operator screens of the Sessions lot are served.
 *
 * ## Why a file of its own beside the two that were already there
 *
 * `session.ts` is the contract the Expériences and Protocoles lots SHARE, and
 * nothing here belongs in it: no lot has to agree on these shapes in order to
 * write its own rows. `session-play.ts` is what the PERSON's browser reads,
 * plus the operator's list, and it says so. This file is the third reader:
 * somebody who opens a session that is already over and walks it.
 *
 * Nothing below redraws a stored shape. A turn is `sessionTurnSchema` entire
 * (issue #67), a traversal is `sessionPageVisitSchema` entire, and a line of the
 * list is `sessionRowSchema`. What is ADDED is the frozen cascade read back by
 * name - the one thing the screens cannot get from a stored row, because a
 * session records version identifiers and a screen shows names.
 */

/* ------------------------------------------------------- the frozen cascade */

/**
 * One object of the cascade, as the session PLAYED it (§5.2, §12.3).
 *
 * The identity travels beside the version: `id` is the entity, so « lire le
 * protocole tel qu'il a été joué » can put a `?version=` link under the reader,
 * and `versionNumber` is what the screens print beside the name (« Entretien
 * fondateur v5 »). Both come from the FROZEN version and never from the
 * object's current pointers - a version passed to production since must not
 * rewrite what a session ran on.
 *
 * `name` is the CURRENT name of the entity and not the frozen one, and the
 * asymmetry is the one §12.1 already makes for the experience: an object
 * renamed since is one object with one current name, and Sessions is the
 * operator's screen. What ran is the version; what it is called is today.
 */
const playedObjectShape = {
  id: z.string().min(1),
  name: z.string().min(1),
  versionId: z.string().min(1),
  versionNumber: z.number().int().positive(),
};

export const playedProtocolSchema = z
  .object({
    ...playedObjectShape,
    /**
     * The protocol ENTIRE, as it was frozen: step directions, objectives,
     * criteria and thresholds, general rules, exit conditions.
     *
     * It travels because every annotation of §12.2 and §12.3 is read against it
     * and against nothing a turn carries: a turn names a `stepId`, and the
     * step's name, its rank and its objectives live here. Serving the
     * identifiers alone would mean a round trip per step name, against a
     * version the screen cannot address - a frozen version is reached through
     * the session, never through the protocol's own history.
     */
    content: protocolContentSchema,
  })
  .strict();
export type PlayedProtocol = z.infer<typeof playedProtocolSchema>;

export const playedCriterionSchema = z
  .object({
    ...playedObjectShape,
    /** « Le nom d'un critère ouvre sa fiche avec sa direction FIGÉE » (§12.3). */
    content: criterionContentSchema,
  })
  .strict();
export type PlayedCriterion = z.infer<typeof playedCriterionSchema>;

/**
 * The persona, named and versioned and nothing more.
 *
 * No content, and that is deliberate: what a persona holds is the direction,
 * the tone and the involvement level served to the INTERVIEWER, and §8.7 puts
 * the record of what a seat was actually served in `session_turn_prompts`,
 * behind its own route. Copying the persona here would be a second, prettier
 * answer to « qu'est-ce que l'interviewer a reçu », and the two would part
 * company the first time an overlay is applied.
 */
export const playedPersonaSchema = z.object(playedObjectShape).strict();
export type PlayedPersona = z.infer<typeof playedPersonaSchema>;

/**
 * The whole cascade a session froze (§5.2), resolved back to objects.
 *
 * Every member is nullable or empty where the session froze nothing - a page
 * carrying no protocol, a persona chosen « plus tard » - and ALSO where the
 * frozen version no longer reads. A screen that could not draw because one of
 * four objects failed a parse would lose the parcours, the transcript and the
 * figures over a name it only wanted to print.
 */
export const playedCascadeSchema = z
  .object({
    protocol: playedProtocolSchema.nullable(),
    persona: playedPersonaSchema.nullable(),
    /** Those the frozen protocol cites (§5.2), in the order the session froze them. */
    criteria: z.array(playedCriterionSchema),
  })
  .strict();
export type PlayedCascade = z.infer<typeof playedCascadeSchema>;

/* ------------------------------------------------------------- the walk */

/**
 * One value the person typed, as the parcours shows it (§12.2): the label it
 * was written under, and what was written.
 *
 * The NAME and not the block's current label: `sessionVariableSchema` freezes it
 * beside the value precisely so a reader of an old session shows the name that
 * session knew (§8.6). The block id travels too, because a value is shown on the
 * page whose block wrote it and a screen has to be able to say which.
 *
 * The value keeps its type: « une case cochée se lit "coché" et jamais `true` »
 * is a rendering, and rendering is the screen's business - flattening a boolean
 * to a string here would take that choice away from it.
 */
export const sessionNamedValueSchema = z
  .object({
    blockId: z.string().min(1),
    name: z.string().min(1),
    value: z.union([z.string(), z.boolean()]),
  })
  .strict();
export type SessionNamedValue = z.infer<typeof sessionNamedValueSchema>;

/**
 * One traversed page with what was typed on it - the stored visit, EXTENDED.
 *
 * `.extend()` and not a redrawing: `sessionPageVisitSchema` stays the shape of
 * the row (#67), and this adds the one thing the parcours needs that no row
 * holds. The session stores its values keyed by block id in one flat record
 * (§8.6), so somebody has to put each value back on the page it was typed on,
 * and the server is that somebody - « les valeurs saisies se lisent sur la page
 * où elles ont été tapées », and a screen doing the grouping itself would need
 * the whole frozen experience to do it.
 */
export const sessionWalkPageSchema = sessionPageVisitSchema.extend({
  values: z.array(sessionNamedValueSchema),
});
export type SessionWalkPage = z.infer<typeof sessionWalkPageSchema>;

/* -------------------------------------------------- the inventory (#172) */

/**
 * What a variable of the inventory is (§12.2): a field, a checkbox, a retained
 * choice, the noyau - and, since issue #176, a `selection` (the option retained
 * by a Sélection, a text) and a `note` (the stars given by a Note, a text
 * holding an integer from 1 to 5, which Sessions words « 4 / 5 »). Their
 * natures are distinct from `field`: the screen colours and names them apart
 * (retour-experience.md §4.1, §4.2, §8).
 */
export const sessionVariableKindSchema = z.enum([
  'field',
  'checkbox',
  'choice',
  'noyau',
  'selection',
  'note',
]);
export type SessionVariableKind = z.infer<typeof sessionVariableKindSchema>;

/**
 * Why a declared variable holds no value (§12.2) - each DEDUCED from what the
 * session already stored, nothing new is written:
 *
 * - `page-never-reached` - its page is not among the visits;
 * - `page-current` - the session is still on its page;
 * - `hidden-by-condition` - its page was visited, the block carries a display
 *   condition (§9.2) and no value was written;
 * - `left-empty` - the value written is empty;
 * - `noyau-undetermined` - the Noyau block recorded no first choice.
 */
export const sessionUnfilledReasonSchema = z.enum([
  'page-never-reached',
  'page-current',
  'hidden-by-condition',
  'left-empty',
  'noyau-undetermined',
]);
export type SessionUnfilledReason = z.infer<typeof sessionUnfilledReasonSchema>;

/**
 * One line of the inventory (§12.2, issue #172): a variable the FROZEN
 * experience declares, filled or not.
 *
 * - `key` is what the session stores it under: the block id of a field or a
 *   checkbox, the page id of a retained choice, `noyau` for `@noyau`.
 * - `name` is the frozen name when a value was written (§8.6), the declared
 *   one otherwise.
 * - Exactly one of `value` and `reason` is non-null. A checkbox's value stays a
 *   boolean (« coché » is a rendering); the noyau's value is its name.
 * - `page` is the page that produces it: its id and name in the frozen
 *   experience, and `visit`, the 0-based position in `SessionDetail.pages` of
 *   its LAST visit (a reprise gives the last), null when never reached.
 */
export const sessionInventoryEntrySchema = z
  .object({
    key: z.string().min(1),
    name: z.string().min(1),
    kind: sessionVariableKindSchema,
    value: z.union([z.string(), z.boolean()]).nullable(),
    reason: sessionUnfilledReasonSchema.nullable(),
    page: z
      .object({
        pageId: z.string().min(1),
        name: z.string(),
        visit: z.number().int().nonnegative().nullable(),
      })
      .strict(),
  })
  .strict()
  .refine((entry) => (entry.value === null) !== (entry.reason === null), {
    message: 'exactly one of value and reason',
  });
export type SessionInventoryEntry = z.infer<typeof sessionInventoryEntrySchema>;

/* --------------------------------------------------------------- the detail */

/**
 * What the determination of the noyau SPENT (noyau.md §4.7, §9.1, #149): the
 * cost its row stores - the sum of the analyst's calls - and their tokens,
 * every call summed by `turnCallTokens`.
 *
 * Served beside the turns rather than folded into them, because the analyst
 * never plays a turn: « le coût de l'analyste s'ajoute au coût de la session et
 * ses jetons à ses jetons », and `sessionFigures` is the one place that adds it,
 * so the list, the parcours and the MCP read the same euro. The cost is the
 * very column the list adds (`noyauCostsOf`), never a second sum.
 *
 * The figures and nothing else: the notes, the pair and the calls themselves
 * are what the lot of Sessions serves (#152).
 */
export const sessionNoyauUsageSchema = z
  .object({
    costEur: z.number().nonnegative(),
    tokens: z.number().int().nonnegative(),
  })
  .strict();
export type SessionNoyauUsage = z.infer<typeof sessionNoyauUsageSchema>;

/* ---------------------------------------------------------- the noyau read */

/**
 * What a card of a choice WAS (noyau.md §9.1): the principal or the second at
 * the first choice, the retained noyau or its opposite at the second.
 *
 * DERIVED at the read, from the determination and the first choice's card -
 * the choice row stores the noyau of each card and nothing of its place, so
 * nothing is stored twice (N14). When the opposite of the retained IS the
 * second of the first choice (N18), the second choice still reads « retenu »
 * and « opposé »: the role is the card's in ITS choice.
 */
export const noyauCardRoleSchema = z.enum(['principal', 'second', 'retained', 'opposite']);
export type NoyauCardRole = z.infer<typeof noyauCardRoleSchema>;

/**
 * One card of a choice as the OPERATOR reads it (§9.1): its opaque identifier,
 * the noyau behind it - which the person never saw (N19, N28), and which the
 * operator's screens name and colour - what it was, and the text AS SERVED,
 * read off the version of the texts frozen on the session.
 *
 * `role` is null only on a card that matches none of its choice's standings -
 * a row that disagrees with its own determination, which no writer produces.
 * `text` is null when the frozen version of the texts no longer reads.
 */
export const sessionNoyauCardReadingSchema = z
  .object({
    cardId: noyauCardIdSchema,
    noyau: noyauCodeSchema,
    role: noyauCardRoleSchema.nullable(),
    text: richTextSchema.nullable(),
  })
  .strict();
export type SessionNoyauCardReading = z.infer<typeof sessionNoyauCardReadingSchema>;

/**
 * One choice of the session, read (§9.1, §10): its page and block, its two
 * cards IN THE ORDER SHOWN - left on a screen, on top on a telephone - the
 * moment they were shown, and the card CONFIRMED with its noyau and its moment.
 * `taken` is null while the page has not been left by « Choisir ce texte ».
 *
 * Since issue #169, what the first choice made of the retained noyau
 * (noyau.md §3.8, N74, N76, N77), read by `noyauRetention` and null on a second
 * choice:
 *
 * - `maxGap` - the threshold frozen at the deal, null on a first choice dealt
 *   before #169 (« sans seuil », « avant le seuil »);
 * - `gap` - the note of the principal minus the note of the second;
 * - `decides` - whether the card taken gave the retained noyau: true within the
 *   threshold or without one, false beyond it (« sans effet »), null until a
 *   card is taken.
 */
export const sessionNoyauChoiceReadingSchema = z
  .object({
    mode: noyauChoiceModeSchema,
    pageId: experienceLocalIdSchema,
    blockId: experienceLocalIdSchema,
    cards: z.tuple([sessionNoyauCardReadingSchema, sessionNoyauCardReadingSchema]),
    shownAt: z.string().datetime(),
    takenCardId: noyauCardIdSchema.nullable(),
    taken: noyauCodeSchema.nullable(),
    chosenAt: z.string().datetime().nullable(),
    maxGap: noyauMaxGapSchema.nullable(),
    gap: z.number().int().nullable(),
    decides: z.boolean().nullable(),
  })
  .strict();
export type SessionNoyauChoiceReading = z.infer<typeof sessionNoyauChoiceReadingSchema>;

/**
 * Where a Noyau block sits in the FROZEN experience - what tells the parcours
 * which traversed page was a page of choice or of portrait, since a visit
 * records a page and nothing of what it carried.
 */
export const sessionNoyauBlockSiteSchema = z
  .object({
    pageId: experienceLocalIdSchema,
    blockId: experienceLocalIdSchema,
    mode: noyauBlockModeSchema,
  })
  .strict();
export type SessionNoyauBlockSite = z.infer<typeof sessionNoyauBlockSiteSchema>;

/**
 * The version of the texts of the noyaux the session FROZE at its start
 * (§3.5), with its number - « Les textes des noyaux v2 » - and the pin the
 * experience's reference carried. `versionNumber` is null when the version no
 * longer reads, `pin` when the frozen experience no longer does.
 */
export const sessionNoyauTextsSchema = z
  .object({
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive().nullable(),
    pin: versionPinSchema.nullable(),
  })
  .strict();
export type SessionNoyauTexts = z.infer<typeof sessionNoyauTextsSchema>;

/**
 * The SEQUENCE OF THE NOYAU as the operator reads it (noyau.md §9.1, §9.3,
 * §10; issue #152) - the parcours, the two exports and `get_session` are all
 * drawn from it.
 *
 * - `blocks` - the Noyau blocks of the frozen experience, page by page;
 * - `texts` - the version of the texts frozen at the start, null when the
 *   session froze none;
 * - `determination` - the row ENTIRE, as `session/noyau.ts` reads it: the
 *   state, the six notes and their sentences, the principal and the second,
 *   the draw, every call of the analyst with its raw answer and its prompt
 *   version, the cost, the two moments. Null while there is none;
 * - `choices` - the first before the second, each only once it was dealt;
 * - `retained` - the retained noyau, by the rule of `noyauRetention` (§3.8,
 *   issue #169): the card confirmed at the first choice within its threshold,
 *   the principal beyond it - the one reading of it the advance plays too;
 * - `portrait` - the portrait of the retained noyau AS SERVED: only once the
 *   session reached a page that shows it.
 *
 * What is NOT here is « l'entrée servie » to the analyst (§8): the largest
 * thing a determination produces and the least often read, behind its own
 * route like the prompts of a turn - and so absent from the JSON export too
 * (N21).
 */
export const sessionNoyauReadingSchema = z
  .object({
    blocks: z.array(sessionNoyauBlockSiteSchema),
    texts: sessionNoyauTextsSchema.nullable(),
    determination: noyauDeterminationSchema.nullable(),
    choices: z.array(sessionNoyauChoiceReadingSchema),
    retained: noyauCodeSchema.nullable(),
    portrait: richTextSchema.nullable(),
  })
  .strict();
export type SessionNoyauReading = z.infer<typeof sessionNoyauReadingSchema>;

/**
 * `GET /api/sessions/{id}` - one session entire, for the parcours of §12.2 and
 * the conversation of §12.3.
 *
 * ONE call for two screens, because they are two views of one reading: a
 * conversation opened from a parcours must not re-fetch what the parcours
 * already held. What is NOT here is the one thing §8.7 puts behind its own
 * route - the prompts served to each seat, « la plus grosse chose qu'un tour
 * produit et la moins souvent lue ».
 *
 * `pages` is in the order the session traversed them, oldest first, and a page
 * entered twice appears twice: a reprise (§5.3) walks a page again, and the
 * parcours shows it again.
 *
 * `turns` is in the order they were played, `index` ascending, across the whole
 * session and not per step.
 *
 * `noyauUsage` is null on a session with no determination of the noyau - an
 * experience without a Noyau block, or a protocol not closed yet.
 *
 * `noyau` is null on a session whose frozen experience carries no Noyau block
 * (issue #152): the sequence was never part of its timeline.
 */
/**
 * One frozen version of a setting singleton, as the parcours names it: the
 * identifier the session froze and the number the operator reads (`v5`).
 */
export const frozenSettingVersionSchema = z
  .object({ versionId: z.string().min(1), number: z.number().int().positive() })
  .strict();
export type FrozenSettingVersion = z.infer<typeof frozenSettingVersionSchema>;

/**
 * The versions of « Modèles » and of « Règles » a session froze at its start
 * (vitrine.md section 5, issue #192) - the line « Modèles v5 · Règles v3 » of its
 * parcours. Each is null on a session started before the issue, which froze
 * nothing and reads what is current on its side at every turn.
 */
export const sessionSettingsSchema = z
  .object({
    models: frozenSettingVersionSchema.nullable(),
    rules: frozenSettingVersionSchema.nullable(),
  })
  .strict();
export type SessionSettings = z.infer<typeof sessionSettingsSchema>;

/**
 * What the person pressed on a page of the thread (prolongation.md §5, §7): a
 * `card` of a Noyau block - `text` is the text of the card taken - or a
 * `button`, whose `text` is its label. A Lien and a Texte copiable make no
 * click.
 */
export const sessionBubbleClickSchema = z.object({
  kind: z.enum(['card', 'button']),
  text: z.string(),
});
export type SessionBubbleClick = z.infer<typeof sessionBubbleClickSchema>;

/**
 * ONE bubble of a page played in the conversation, as the operator reads it
 * (prolongation.md §7, §8, issue #186): what Sessions, the exports and the
 * assistant draw between the last turn and the farewell.
 *
 * A bubble is not a turn: no number, no scribe, no objectives (§4.8). It carries
 * the call that wrote it - failed or not, § 4.6 - and its cost, which
 * `sessionFigures` adds to the session's. `pageName` is the FROZEN name of the
 * page, as a page visit's is; `visitIndex` the visit of the page (a reprise walks
 * a page again, and the bubble is written once per visit). `click` is what the
 * person pressed to leave the page, null while it stands there.
 *
 * What was served to write it is read apart, at the shape of a turn's inputs
 * (`turnPromptsSchema`, with the bubble's id in `turnId`).
 *
 * Not `.strict()`, like every stored shape (§4.1).
 */
export const sessionConversationBubbleSchema = z.object({
  id: z.string().min(1),
  pageId: z.string().min(1),
  pageName: z.string(),
  visitIndex: z.number().int().nonnegative(),
  message: z.string(),
  call: turnCallSchema,
  costEur: z.number().nonnegative(),
  createdAt: z.string().datetime(),
  click: sessionBubbleClickSchema.nullable().default(null),
});
export type SessionConversationBubble = z.infer<typeof sessionConversationBubbleSchema>;

export const sessionDetailSchema = z
  .object({
    session: sessionRowSchema,
    pages: z.array(sessionWalkPageSchema),
    turns: z.array(sessionTurnSchema),
    /**
     * The bubbles of the pages played in the conversation (prolongation.md §7,
     * §8, issue #186), in the order they were written, oldest first. Empty on a
     * session with no page of the thread, and on every session written before the
     * issue. Filled by the Lectures lot (B); the shape is this contract's.
     */
    conversationBubbles: z.array(sessionConversationBubbleSchema).default([]),
    cascade: playedCascadeSchema,
    noyauUsage: sessionNoyauUsageSchema.nullable(),
    noyau: sessionNoyauReadingSchema.nullable(),
    /**
     * The rangement of the matter (scenario-succes.md §3, §7, issue #177): null
     * on a session whose protocol had no objective target, or that was never
     * ranged. Written by the engine since lot A; null until then.
     */
    rangement: sessionRangementSchema.nullable(),
    /**
     * The inventory of §12.2 (#172): filled first in timeline order, then the
     * declared ones left unfilled. Empty when the experience declares nothing.
     */
    variables: z.array(sessionInventoryEntrySchema),
    /**
     * The REPLAY block (rejeu.md sections 11.3 and 12, issue #193): the mark's
     * facts, the source text and the instruction as served. Null on a session
     * Claude did not play; OPTIONAL at the régime of `feedbackSubmittedAt`, so a
     * detail written before the replay still parses (`detail.replay ?? null`).
     * The assistant serves the light part by default and the two heavy texts
     * behind its layer `replay`.
     */
    replay: sessionReplayFullSchema.nullable().optional(),
    /**
     * The settings the session froze (vitrine.md section 5, issue #192): OPTIONAL
     * at the régime of `replay`, so a detail written before the issue still
     * parses, and a reader that does not know about it draws nothing.
     */
    settings: sessionSettingsSchema.optional(),
  })
  .strict();
export type SessionDetail = z.infer<typeof sessionDetailSchema>;
